// Admin view for the ticket-based support system that replaced the old
// Tawk.to live chat on the buyer side (see client/src/components/SupportWidget.jsx).
import {
  CircleCheckBigIcon,
  ClockIcon,
  MailIcon,
  React,
  RefreshCwIcon,
  SearchIcon,
  TicketIcon,
  UserIcon,
  XIcon,
  motion,
} from "../vendor/runtime.js";
import { api } from "../services/api.js";

const STATUS_META = {
  open: { label: "Open", color: "bg-amber-100 text-amber-800 border-amber-200", Icon: ClockIcon },
  answered: { label: "Answered", color: "bg-green-100 text-green-800 border-green-200", Icon: CircleCheckBigIcon },
  closed: { label: "Closed", color: "bg-gray-100 text-gray-700 border-gray-200", Icon: CircleCheckBigIcon },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.open;
  const Icon = meta.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${meta.color}`}>
      <Icon className={"w-3.5 h-3.5"} />
      {meta.label}
    </span>
  );
};

const SupportTicketsPage = () => {
  const [tickets, setTickets] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [search, setSearch] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("all");

  const [activeTicket, setActiveTicket] = React.useState(null);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalLoading, setModalLoading] = React.useState(false);
  const [replyText, setReplyText] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [updatingStatus, setUpdatingStatus] = React.useState(false);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const res = await api.get("/support-tickets");
      setTickets(res.data || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching tickets:", err);
      setError("Failed to load support tickets");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchTickets();
  }, []);

  const counts = {
    open: tickets.filter((t) => t.status === "open").length,
    answered: tickets.filter((t) => t.status === "answered").length,
    closed: tickets.filter((t) => t.status === "closed").length,
  };

  const filtered = tickets.filter((t) => {
    const q = search.toLowerCase();
    const matchesSearch =
      t.subject?.toLowerCase().includes(q) ||
      t.user_name?.toLowerCase().includes(q) ||
      t.user_email?.toLowerCase().includes(q);
    const matchesStatus = statusFilter === "all" || t.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const openTicket = async (id) => {
    setModalOpen(true);
    setModalLoading(true);
    setReplyText("");
    try {
      const res = await api.get(`/support-tickets/${id}`);
      setActiveTicket(res.data);
    } catch (err) {
      console.error("Error loading ticket:", err);
    } finally {
      setModalLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setActiveTicket(null);
    setReplyText("");
  };

  const sendReply = async () => {
    if (!replyText.trim() || !activeTicket || sending) return;
    setSending(true);
    try {
      await api.post(`/support-tickets/${activeTicket.id}/reply`, { message: replyText.trim() });
      setReplyText("");
      const res = await api.get(`/support-tickets/${activeTicket.id}`);
      setActiveTicket(res.data);
      fetchTickets();
    } catch (err) {
      console.error("Error sending reply:", err);
      alert(err.response?.data?.error || "Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  const changeStatus = async (status) => {
    if (!activeTicket || updatingStatus) return;
    setUpdatingStatus(true);
    try {
      await api.patch(`/support-tickets/${activeTicket.id}/status`, { status });
      setActiveTicket({ ...activeTicket, status });
      fetchTickets();
    } catch (err) {
      console.error("Error updating status:", err);
      alert("Failed to update status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Unified thread: the ticket's opening message, then every reply, in order.
  const threadMessages = activeTicket
    ? [
        { role: "user", message: activeTicket.message, created_at: activeTicket.created_at },
        ...activeTicket.replies.map((r) => ({
          role: r.sender_role,
          message: r.message,
          created_at: r.created_at,
        })),
      ]
    : [];

  const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  };
  const stagger = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  if (loading) {
    return (
      <div className={"admin-page flex items-center justify-center bg-gradient-to-br from-purple-50 to-purple-100"}>
        <RefreshCwIcon className={"w-10 h-10 text-purple-600 animate-spin"} />
      </div>
    );
  }

  return (
    <div className={"admin-page bg-gradient-to-br from-purple-50 via-purple-100 to-purple-50"}>
      <main className="admin-main">
        <motion.div initial={"hidden"} animate={"visible"} variants={fadeUp} className={"mb-10"}>
          <span className={"inline-block py-1 px-3 mb-3 bg-purple-100 text-purple-800 rounded-full text-sm font-medium"}>
            {"Support"}
          </span>
          <h1 className={"text-3xl md:text-4xl font-bold text-gray-900 mb-2"}>
            {"Support "}
            <span className={"bg-gradient-to-r from-purple-600 to-purple-800 bg-clip-text text-transparent"}>
              {"Tickets"}
            </span>
          </h1>
          <p className={"text-gray-600"}>{"Buyer support tickets — reply here and it shows up in their ticket thread on the site."}</p>
        </motion.div>

        <motion.div initial={"hidden"} animate={"visible"} variants={stagger} className={"grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8"}>
          {[
            { key: "open", label: "Open", value: counts.open, color: "bg-amber-100 text-amber-700", hoverBorder: "hover:border-amber-300", Icon: ClockIcon },
            { key: "answered", label: "Answered", value: counts.answered, color: "bg-green-100 text-green-700", hoverBorder: "hover:border-green-300", Icon: CircleCheckBigIcon },
            { key: "closed", label: "Closed", value: counts.closed, color: "bg-gray-100 text-gray-600", hoverBorder: "hover:border-gray-300", Icon: TicketIcon },
          ].map(({ key, label, value, color, hoverBorder, Icon }) => (
            <motion.div
              key={key}
              variants={fadeUp}
              className={`bg-white rounded-2xl border border-gray-100 p-4 sm:p-5 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg ${hoverBorder}`}
            >
              <div className={"flex items-start justify-between gap-3"}>
                <div className={"flex-1 min-w-0"}>
                  <p className={"text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 truncate"}>{label}</p>
                  <div className={"text-2xl sm:text-3xl font-extrabold text-gray-900 leading-tight"}>{value}</div>
                </div>
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg ${color} flex items-center justify-center flex-shrink-0`}>
                  <Icon className={"w-5 h-5 sm:w-6 sm:h-6"} />
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div initial={"hidden"} animate={"visible"} variants={fadeUp} className={"mb-8"}>
          <div className={"bg-white rounded-2xl shadow-lg p-6 border border-gray-100"}>
            <div className={"flex flex-col lg:flex-row gap-4"}>
              <div className={"flex-1 relative"}>
                <SearchIcon className={"absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5"} />
                <input
                  type={"text"}
                  placeholder={"Search by subject, name, or email..."}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className={"w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"}
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={"bg-white border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"}
              >
                <option value={"all"}>{"All Status"}</option>
                <option value={"open"}>{"Open"}</option>
                <option value={"answered"}>{"Answered"}</option>
                <option value={"closed"}>{"Closed"}</option>
              </select>
              <button
                onClick={fetchTickets}
                className={"bg-purple-600 text-white px-6 py-3 rounded-xl hover:bg-purple-700 transition duration-200 flex items-center justify-center"}
              >
                <RefreshCwIcon className={"w-5 h-5 mr-2"} />
                {"Refresh"}
              </button>
            </div>
          </div>
        </motion.div>

        <motion.div initial={"hidden"} animate={"visible"} variants={fadeUp}>
          <div className={"bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100"}>
            {error ? (
              <div className={"p-8 text-center text-red-600"}>{error}</div>
            ) : filtered.length === 0 ? (
              <div className={"p-12 text-center"}>
                <TicketIcon className={"w-12 h-12 text-gray-300 mx-auto mb-3"} />
                <p className={"text-gray-500"}>{"No tickets found."}</p>
              </div>
            ) : (
              <div className={"overflow-x-auto"}>
                <table className={"min-w-full divide-y divide-gray-200"}>
                  <thead className={"bg-gray-50"}>
                    <tr>
                      <th className={"px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"}>{"Subject"}</th>
                      <th className={"px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"}>{"From"}</th>
                      <th className={"px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"}>{"Status"}</th>
                      <th className={"px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"}>{"Updated"}</th>
                      <th className={"px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"}>{"Action"}</th>
                    </tr>
                  </thead>
                  <tbody className={"divide-y divide-gray-100"}>
                    {filtered.map((t) => (
                      <tr key={t.id} className={"hover:bg-gray-50 transition-colors"}>
                        <td className={"px-6 py-4"}>
                          <div className={"text-sm font-medium text-gray-900 max-w-xs truncate"}>{t.subject}</div>
                          {t.reply_count > 0 && (
                            <div className={"text-xs text-gray-400"}>
                              {t.reply_count} repl{t.reply_count === 1 ? "y" : "ies"}
                            </div>
                          )}
                        </td>
                        <td className={"px-6 py-4"}>
                          <div className={"flex items-center"}>
                            <div className={"w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mr-3 flex-shrink-0"}>
                              <UserIcon className={"w-4 h-4 text-purple-600"} />
                            </div>
                            <div className={"min-w-0"}>
                              <div className={"text-sm font-medium text-gray-900 truncate"}>{t.user_name}</div>
                              <div className={"text-xs text-gray-500 flex items-center truncate"}>
                                <MailIcon className={"w-3 h-3 mr-1 flex-shrink-0"} />
                                {t.user_email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className={"px-6 py-4"}>
                          <StatusBadge status={t.status} />
                        </td>
                        <td className={"px-6 py-4 text-sm text-gray-500 whitespace-nowrap"}>
                          {new Date(t.updated_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </td>
                        <td className={"px-6 py-4 text-right"}>
                          <button
                            onClick={() => openTicket(t.id)}
                            className={"px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"}
                          >
                            {"View & Reply"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </motion.div>
      </main>

      {modalOpen && (
        <div className={"fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={"bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"}
          >
            <div className={"px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0"}>
              <div className={"min-w-0"}>
                <h3 className={"text-lg font-bold text-gray-900 truncate"}>
                  {activeTicket ? activeTicket.subject : "Loading..."}
                </h3>
                {activeTicket && (
                  <p className={"text-sm text-gray-500"}>
                    {activeTicket.user_name} · {activeTicket.user_email}
                  </p>
                )}
              </div>
              <button onClick={closeModal} className={"p-2 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"}>
                <XIcon className={"w-5 h-5 text-gray-500"} />
              </button>
            </div>

            {modalLoading || !activeTicket ? (
              <div className={"flex-1 flex items-center justify-center py-16"}>
                <RefreshCwIcon className={"w-8 h-8 text-purple-600 animate-spin"} />
              </div>
            ) : (
              <>
                <div className={"px-6 py-3 border-b border-gray-100 flex items-center justify-between flex-shrink-0"}>
                  <StatusBadge status={activeTicket.status} />
                  <div className={"flex gap-2"}>
                    {activeTicket.status !== "closed" ? (
                      <button
                        onClick={() => changeStatus("closed")}
                        disabled={updatingStatus}
                        className={"text-xs font-medium text-gray-600 hover:text-gray-800 px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"}
                      >
                        {"Close Ticket"}
                      </button>
                    ) : (
                      <button
                        onClick={() => changeStatus("open")}
                        disabled={updatingStatus}
                        className={"text-xs font-medium text-purple-600 hover:text-purple-800 px-3 py-1.5 border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors"}
                      >
                        {"Reopen Ticket"}
                      </button>
                    )}
                  </div>
                </div>

                <div className={"flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50"}>
                  {threadMessages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === "admin" ? "justify-end" : "justify-start"}`}>
                      <div
                        className={`max-w-[80%] rounded-xl px-4 py-3 text-sm ${
                          m.role === "admin" ? "bg-purple-600 text-white" : "bg-white border border-gray-200 text-gray-800"
                        }`}
                      >
                        <p className={"whitespace-pre-wrap break-words"}>{m.message}</p>
                        <p className={`text-[10px] mt-1.5 ${m.role === "admin" ? "text-purple-200" : "text-gray-400"}`}>
                          {m.role === "admin" ? "You (Support)" : activeTicket.user_name}
                          {" · "}
                          {new Date(m.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className={"p-4 border-t border-gray-200 flex-shrink-0"}>
                  <div className={"flex gap-2"}>
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder={"Type your reply..."}
                      rows={2}
                      className={"flex-1 px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none text-sm"}
                    />
                    <button
                      onClick={sendReply}
                      disabled={sending || !replyText.trim()}
                      className={"px-5 py-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:bg-purple-300 disabled:cursor-not-allowed transition-colors font-medium text-sm flex-shrink-0"}
                    >
                      {sending ? <RefreshCwIcon className={"w-4 h-4 animate-spin"} /> : "Send Reply"}
                    </button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export { SupportTicketsPage };
